import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArrowLeftRight, Wallet, Building2, Users2, ArrowRight, ShieldCheck, Clipboard, AlertTriangle,
} from "lucide-react";
import Header from "@/components/Header";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function TxHistory({ items, filter, title }) {
  const list = items.filter(filter);
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Clipboard size={16} className="text-purple-600" />
        <h3 className="font-bold text-slate-900">{title}</h3>
      </div>
      {list.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm">No transactions yet</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.map((t) => {
            const dt = new Date(t.created_at);
            const positive = (t.delta ?? 0) > 0;
            return (
              <li key={t.id} className="flex items-center gap-3 px-4 py-3">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                  positive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
                }`}>
                  <ArrowRight size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{t.note}</div>
                  <div className="text-xs text-slate-500">
                    {dt.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}
                    {t.status !== "completed" && (
                      <span className="ml-2 text-amber-600 font-semibold uppercase tracking-widest">{t.status}</span>
                    )}
                  </div>
                </div>
                <div className={`text-sm font-extrabold tabular-nums ${positive ? "text-emerald-600" : "text-rose-600"}`}>
                  {positive ? "+" : ""}{INR(t.delta ?? t.amount)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function FundTransfer() {
  const [balances, setBalances] = useState({ main_wallet: 0, e_wallet: 0 });
  const [items, setItems] = useState([]);
  const [amountM, setAmountM] = useState("");
  const [amountB, setAmountB] = useState("");
  const [amountU, setAmountU] = useState("");
  const [recipient, setRecipient] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [kycVerified, setKycVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const location = useLocation();

  const load = async () => {
    try {
      const [b, t, k] = await Promise.all([
        api.get("/wallet/balances"),
        api.get("/wallet/transactions"),
        api.get("/kyc/my"),
      ]);
      setBalances(b.data);
      setItems(t.data.items || []);
      setKycVerified(k.data?.kyc?.status === "verified");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const bankSecEl = useRef(null);
  const userSecEl = useRef(null);
  const mainSecEl = useRef(null);
  useEffect(() => {
    const anchor = location.hash.replace("#", "");
    const map = { bank: bankSecEl, user: userSecEl, main: mainSecEl };
    if (map[anchor]?.current) {
      setTimeout(() => map[anchor].current.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash]);

  const lookup = async () => {
    setRecipientName("");
    const code = recipient.trim();
    if (!code) return;
    try {
      const { data } = await api.get("/wallet/lookup", { params: { user_code: code } });
      setRecipientName(data.name);
    } catch (e) { setRecipientName(""); toast.error(formatApiError(e)); }
  };

  const submit = async (endpoint, body, resetFn, msg) => {
    setBusy(true);
    try {
      await api.post(endpoint, body);
      toast.success(msg);
      resetFn();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cards = useMemo(() => ([
    { key: "main", title: "Transfer to E-Wallet", desc: "Move funds for P2P transfers",
      cls: "bg-emerald-50 border-emerald-200 hover:border-emerald-400",
      iconCls: "text-emerald-600",
      onClick: () => mainSecEl.current?.scrollIntoView({ behavior: "smooth" }), icon: Wallet },
    { key: "bank", title: "Bank Withdrawal", desc: "Withdraw directly to bank (Min ₹100)",
      cls: "bg-rose-50 border-rose-200 hover:border-rose-400",
      iconCls: "text-rose-600",
      onClick: () => bankSecEl.current?.scrollIntoView({ behavior: "smooth" }), icon: Building2 },
    { key: "user", title: "Transfer to user E-wallet", desc: "Send to any user (Min ₹1.00)",
      cls: "bg-sky-50 border-sky-200 hover:border-sky-400",
      iconCls: "text-sky-600",
      onClick: () => userSecEl.current?.scrollIntoView({ behavior: "smooth" }), icon: Users2 },
  ]), []);

  return (
    <div className="min-h-screen" style={{ background: "#560d0d" }}>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16 space-y-5 text-slate-900">
        {/* Balances */}
        <section className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl p-4 shadow bg-gradient-to-br from-violet-600 to-purple-700 text-white">
            <div className="text-[11px] uppercase tracking-widest">Main Wallet</div>
            <div data-testid="main-wallet-balance" className="text-2xl font-black mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              {INR(balances.main_wallet)}
            </div>
          </div>
          <div className="rounded-2xl p-4 shadow bg-gradient-to-br from-teal-500 to-emerald-600 text-white">
            <div className="text-[11px] uppercase tracking-widest">E-Wallet</div>
            <div data-testid="ewallet-balance" className="text-2xl font-black mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              {INR(balances.e_wallet)}
            </div>
          </div>
        </section>

        {/* Transfer Options grid */}
        <section className="bg-white rounded-2xl shadow-lg p-5">
          <h2 className="font-extrabold flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <ArrowLeftRight size={18} className="text-purple-600" /> Transfer Options
          </h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {cards.map((c) => (
              <button
                key={c.key}
                data-testid={`transfer-option-${c.key}`}
                onClick={c.onClick}
                className={`text-left rounded-xl p-4 border-2 hover:shadow-md transition-all ${c.cls}`}
              >
                <c.icon size={20} className={c.iconCls} />
                <div className="mt-2 font-bold text-slate-900">{c.title}</div>
                <div className="text-xs text-slate-600 mt-1">{c.desc}</div>
              </button>
            ))}
          </div>
        </section>

        {/* 1. Transfer to E-Wallet */}
        <section ref={mainSecEl} className="bg-white rounded-2xl shadow-lg p-5 border-2 border-emerald-200">
          <h3 className="font-extrabold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>Transfer to E-Wallet</h3>
          <div className="mt-3 rounded-xl bg-violet-50 p-4">
            <div className="text-xs text-slate-500 font-semibold">Available Balance</div>
            <div className="text-2xl font-black text-purple-700" style={{ fontFamily: "Outfit, sans-serif" }}>
              {INR(balances.main_wallet)}
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-600">Amount to Transfer (Min ₹100)</label>
            <input
              data-testid="main-transfer-amount"
              type="number" min="100" step="1"
              value={amountM} onChange={(e) => setAmountM(e.target.value)}
              placeholder="Enter amount"
              className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
            />
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setAmountM("")} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Cancel</button>
            <button
              data-testid="main-transfer-submit"
              disabled={busy || !amountM}
              onClick={() => submit("/wallet/main-to-ewallet", { amount: Number(amountM) },
                () => setAmountM(""), "Transferred to E-Wallet")}
              className="px-6 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg disabled:opacity-50"
            >
              Transfer
            </button>
          </div>
        </section>

        <TxHistory items={items} filter={(t) => t.type === "main_to_ewallet" && t.affects === "e_wallet"}
                   title="Transfer to E-Wallet Transaction History" />

        {/* 2. Bank Withdrawal */}
        <section ref={bankSecEl} className="bg-white rounded-2xl shadow-lg p-5 border-2 border-rose-200">
          <h3 className="font-extrabold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>Bank Withdrawal</h3>
          <div className="mt-3 rounded-xl bg-violet-50 p-4">
            <div className="text-xs text-slate-500 font-semibold">Available Balance</div>
            <div className="text-2xl font-black text-purple-700" style={{ fontFamily: "Outfit, sans-serif" }}>
              {INR(balances.main_wallet)}
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-600">Amount (Min ₹200)</label>
            <input
              data-testid="bank-withdrawal-amount"
              type="number" min="200" step="1"
              value={amountB} onChange={(e) => setAmountB(e.target.value)}
              placeholder="Enter amount"
              className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-rose-500 outline-none"
            />
          </div>
          {!kycVerified && (
            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 flex gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Your bank details are not available. Please complete your{" "}
                <a href="/profile/kyc" className="font-bold underline">KYC</a> to withdraw to your bank account.
              </span>
            </div>
          )}
          {kycVerified && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-3 py-2 flex gap-2">
              <ShieldCheck size={14} /> KYC verified — bank details auto-used from your KYC.
            </div>
          )}
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setAmountB("")} className="px-4 py-2 text-sm border border-slate-200 rounded-lg">Cancel</button>
            <button
              data-testid="bank-withdrawal-submit"
              disabled={busy || !amountB || !kycVerified}
              onClick={() => submit("/wallet/bank-withdrawal", { amount: Number(amountB) },
                () => setAmountB(""), "Withdrawal request submitted")}
              className="px-6 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg disabled:opacity-50"
            >
              Submit Request
            </button>
          </div>
        </section>

        <TxHistory items={items} filter={(t) => t.type === "bank_withdrawal"}
                   title="Bank Withdrawal Transaction History" />

        {/* 3. Transfer to user E-Wallet */}
        <section ref={userSecEl} className="bg-white rounded-2xl shadow-lg p-5 border-2 border-sky-200">
          <h3 className="font-extrabold text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>Transfer to user E-wallet</h3>
          <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs text-slate-500 font-semibold">Your E-Wallet Balance</div>
            <div className="text-lg font-extrabold">{INR(balances.e_wallet)}</div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-600">User ID</label>
            <input
              data-testid="user-transfer-recipient"
              value={recipient}
              onChange={(e) => { setRecipient(e.target.value); setRecipientName(""); }}
              onBlur={lookup}
              placeholder="Enter recipient User ID"
              className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 outline-none uppercase"
            />
            {recipientName && (
              <div data-testid="user-transfer-recipient-name" className="mt-1 text-sm text-emerald-700 font-bold">
                {recipientName}
              </div>
            )}
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold text-slate-600">Amount (Min ₹1.00)</label>
            <input
              data-testid="user-transfer-amount"
              type="number" min="1" step="1"
              value={amountU} onChange={(e) => setAmountU(e.target.value)}
              placeholder="0"
              className="mt-1 w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 outline-none"
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              data-testid="user-transfer-submit"
              disabled={busy || !amountU || !recipient}
              onClick={() => submit("/wallet/user-transfer",
                { recipient_user_code: recipient.trim(), amount: Number(amountU) },
                () => { setAmountU(""); setRecipient(""); setRecipientName(""); },
                "Transfer sent")}
              className="px-6 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg disabled:opacity-50"
            >
              Submit
            </button>
          </div>
        </section>

        <TxHistory items={items}
                   filter={(t) => t.type === "user_transfer_out" || t.type === "user_transfer_in"}
                   title="Transfer to user E-wallet Transaction History" />
      </main>
    </div>
  );
}
